import { c as _c } from "react/compiler-runtime";
import { IcoCheckCircle16, IcoSettings16 } from '@onefootprint/icons';
import { Divider, Form, Select, type TemplateInputProps } from '@onefootprint/ui';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTimeout } from 'usehooks-ts';
import SettingsSection from '@/components/playbook-builder/components/shared/settings-section';
import type { CustomQuestionsFormData } from '../../types';
import ButtonsSection, { type ButtonsSectionControls } from './buttons-section';
type PageSection = 'basic' | 'buttons' | 'confirmation';
type PageSettingsProps = {
  autocompleteItems?: TemplateInputProps['autocompleteItems'];
  canEdit: boolean;
  /** True only when the author picked the page header, so a button pick keeps focus off the title. */
  focusTitle: boolean;
  openSections: Record<PageSection, boolean>;
  onSectionOpenChange: (section: PageSection, open: boolean) => void;
  buttons: ButtonsSectionControls;
};
const PageSettings = t0 => {
  const $ = _c(91);
  const {
    autocompleteItems,
    canEdit,
    focusTitle,
    openSections,
    onSectionOpenChange,
    buttons
  } = t0;
  const {
    t
  } = useTranslation("playbook-custom-questions");
  const {
    setFocus,
    setValue
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: ["confirmationMode", "choice.saveValue"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [mode, saveValue] = useWatch(t1);
  const isSkip = mode === "skip";
  let t2;
  if ($[1] !== setValue) {
    t2 = next => setValue("confirmationMode", next, {
      shouldDirty: true
    });
    $[1] = setValue;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const setMode = t2;
  let t3;
  if ($[3] !== setFocus) {
    t3 = () => setFocus("title");
    $[3] = setFocus;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useTimeout(t3, canEdit && focusTitle ? 100 : null);
  let t4;
  if ($[5] !== t) {
    t4 = t(_temp);
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = openSections.basic;
  let t6;
  if ($[7] !== onSectionOpenChange) {
    t6 = open => onSectionOpenChange("basic", open);
    $[7] = onSectionOpenChange;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t) {
    t7 = t(_temp2);
    $[9] = t;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t) {
    t8 = t(_temp3);
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== autocompleteItems) {
    t9 = autocompleteItems ?? {};
    $[13] = autocompleteItems;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = <Form.TemplateInput name="title" label={t7} placeholder={t8} size="compact" autocompleteItems={t9} />;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t) {
    t11 = t(_temp4);
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t(_temp5);
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== autocompleteItems) {
    t13 = autocompleteItems ?? {};
    $[23] = autocompleteItems;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t13) {
    t14 = <Form.TemplateInput name="subtitle" label={t11} placeholder={t12} size="compact" autocompleteItems={t13} />;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== openSections.basic || $[30] !== t10 || $[31] !== t14 || $[32] !== t4 || $[33] !== t6) {
    t15 = <SettingsSection title={t4} icon={IcoSettings16} open={t5} onOpenChange={t6}>{t10}{t14}</SettingsSection>;
    $[29] = openSections.basic;
    $[30] = t10;
    $[31] = t14;
    $[32] = t4;
    $[33] = t6;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Divider />;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== onSectionOpenChange) {
    t17 = open_0 => onSectionOpenChange("buttons", open_0);
    $[36] = onSectionOpenChange;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== autocompleteItems || $[39] !== buttons || $[40] !== canEdit || $[41] !== openSections.buttons || $[42] !== t17) {
    t18 = <ButtonsSection {...buttons} open={openSections.buttons} onOpenChange={t17} autocompleteItems={autocompleteItems} canEdit={canEdit} />;
    $[38] = autocompleteItems;
    $[39] = buttons;
    $[40] = canEdit;
    $[41] = openSections.buttons;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Divider />;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t) {
    t20 = t(_temp6);
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const t21 = openSections.confirmation;
  let t22;
  if ($[47] !== onSectionOpenChange) {
    t22 = open_1 => onSectionOpenChange("confirmation", open_1);
    $[47] = onSectionOpenChange;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t) {
    t23 = t(_temp7);
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  const t24 = !canEdit;
  const t25 = isSkip ? "skip" : "confirm";
  let t26;
  if ($[51] !== setMode) {
    t26 = value => setMode(value === "skip" ? "skip" : "confirm");
    $[51] = setMode;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t) {
    t27 = t(_temp8);
    $[53] = t;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== t27) {
    t28 = {
      value: "confirm",
      label: t27
    };
    $[55] = t27;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== t) {
    t29 = t(_temp9);
    $[57] = t;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== saveValue || $[60] !== t) {
    t30 = saveValue && {
      disabled: true,
      description: t(_temp10)
    };
    $[59] = saveValue;
    $[60] = t;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  let t31;
  if ($[62] !== t29 || $[63] !== t30) {
    t31 = {
      value: "skip",
      label: t29,
      ...t30
    };
    $[62] = t29;
    $[63] = t30;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== t28 || $[66] !== t31) {
    t32 = [t28, t31];
    $[65] = t28;
    $[66] = t31;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== t23 || $[69] !== t24 || $[70] !== t25 || $[71] !== t26 || $[72] !== t32) {
    t33 = <Select label={t23} size="compact" disabled={t24} value={t25} onChange={t26} options={t32} />;
    $[68] = t23;
    $[69] = t24;
    $[70] = t25;
    $[71] = t26;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== isSkip || $[75] !== t) {
    t34 = isSkip ? t(_temp11) : t(_temp12);
    $[74] = isSkip;
    $[75] = t;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== t34) {
    t35 = <p className="text-caption-2 text-tertiary">{t34}</p>;
    $[77] = t34;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== t33 || $[80] !== t35) {
    t36 = <div className="flex flex-col gap-2">{t33}{t35}</div>;
    $[79] = t33;
    $[80] = t35;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== openSections.confirmation || $[83] !== t20 || $[84] !== t22 || $[85] !== t36) {
    t37 = <SettingsSection title={t20} icon={IcoCheckCircle16} open={t21} onOpenChange={t22}>{t36}</SettingsSection>;
    $[82] = openSections.confirmation;
    $[83] = t20;
    $[84] = t22;
    $[85] = t36;
    $[86] = t37;
  } else {
    t37 = $[86];
  }
  let t38;
  if ($[87] !== t15 || $[88] !== t18 || $[89] !== t37) {
    t38 = <div className="bg-primary border border-tertiary rounded"><div className="flex flex-col">{t15}{t16}{t18}{t19}{t37}</div></div>;
    $[87] = t15;
    $[88] = t18;
    $[89] = t37;
    $[90] = t38;
  } else {
    t38 = $[90];
  }
  return t38;
};
export type { PageSection };
export default PageSettings;
function _temp($) {
  return $.pageSettings.title;
}
function _temp2($_0) {
  return $_0.pageSettings.pageTitle;
}
function _temp3($_1) {
  return $_1.pageSettings.pageTitlePlaceholder;
}
function _temp4($_2) {
  return $_2.pageSettings.pageSubtitle;
}
function _temp5($_3) {
  return $_3.pageSettings.pageSubtitlePlaceholder;
}
function _temp6($_4) {
  return $_4.pageSettings.confirmation.title;
}
function _temp7($_5) {
  return $_5.pageSettings.confirmation.label;
}
function _temp8($_6) {
  return $_6.pageSettings.confirmation.confirm;
}
function _temp9($_7) {
  return $_7.pageSettings.confirmation.skip;
}
function _temp10($_8) {
  return $_8.pageSettings.confirmation.skipUnavailable;
}
function _temp11($_9) {
  return $_9.pageSettings.confirmation.hints.skip;
}
function _temp12($_10) {
  return $_10.pageSettings.confirmation.hints.confirm;
}